import { useSortable } from "@dnd-kit/sortable"
import { TableCell, TableRow } from "@pma/components/common/table"
import React, { CSSProperties, useContext } from "react"
import AddTaskInput from "../add-task-input"
import { Column, flexRender } from "@tanstack/react-table"
import { DictType } from "@pma/types"
import { CSS } from "@dnd-kit/utilities"
import { cn } from "@pma/utils/helpers"
import { ProjectContext } from "@pma/contexts/ProjectContext"
import { GripVertical } from "lucide-react"

interface Props {
  row: any
  getCommonPinningStyles: (column: Column<DictType>) => CSSProperties
  group_id?: number | null | undefined
  className?: string
  onRowClick?: (row: DictType) => void
  context?: "tasks" | "projects" | "users"
  dataLength?: number
  iszIndexApply?: boolean
}

function DraggableRow({
  row,
  getCommonPinningStyles,
  group_id,
  className,
  onRowClick,
  context,
  dataLength = 0,
  iszIndexApply = false,
}: Props) {
  const currentRow = row?.original
  const parentTaskId = currentRow?.parent_tasks_id
  const isSubtask = !!parentTaskId
  const effectiveGroupId = group_id === undefined || group_id === null ? -1 : group_id
  const { isEditing } = useContext(ProjectContext)
  const sortableId =
    context === "projects"
      ? `project_${currentRow.id}`
      : isSubtask
        ? `subtask_${parentTaskId}_${currentRow.id}_${effectiveGroupId}`
        : `task_${currentRow.id}_${effectiveGroupId}`

  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    // id: currentRow.id,
    id: sortableId,
    data: {
      type: context === "projects" ? "Project" : "Task",
      row,
      isSubtask: context === "projects" ? false : isSubtask,
      parentTaskId: context === "projects" ? null : isSubtask ? parentTaskId : null,
      groupId: context === "projects" ? null : group_id,
    },
    // disabled: editMode,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? "transform 200ms ease" : "none",
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : "auto",
    width: "100%",
  }

  return (
    <TableRow
      ref={setNodeRef}
      data-state={row.getIsSelected() && "selected"}
      data-task-id={context === "projects" ? undefined : row?.original.id}
      data-project-id={context === "projects" ? row?.original.id : undefined}
      className={cn(
        parentTaskId && "border-b border-primary/10 bg-slate-200",
        "hover:primary/40 hover:ring-2 hover:ring-inset",
        className,
      )}
      style={{
        height: "110px",
        ...style,
      }}
      onClick={(event) => {
        if (isEditing) return
        // Prevent row click if the target is an interactive element
        const target = event.target as HTMLElement
        if (target.closest(".edit-icon")) return
        if (onRowClick && target.classList.contains("table-cell")) {
          onRowClick(row)
        }
      }}
    >
      {row.getVisibleCells().map((cell: any) => {
        if (cell.column.id === "drag-handle") {
          return (
            <TableCell
              key={cell.id}
              className={cn(parentTaskId && "", "p-1 text-center")}
              style={{
                ...getCommonPinningStyles(cell.column),
                width: cell.column.getSize(),
              }}
            >
              <div className='flex touch-none select-none items-center justify-center'>
                <GripVertical
                  size={20}
                  className='cursor-grab text-muted-foreground focus:outline-none focus:ring-0'
                  onPointerDown={(e) => e.stopPropagation()}
                  {...attributes}
                  {...listeners}
                />
              </div>
            </TableCell>
          )
        }
        return (
          <TableCell
            key={cell.id}
            className={parentTaskId && ""}
            style={{
              ...getCommonPinningStyles(cell.column),
              width: cell.column.getSize(),
              zIndex: cell.column.getIsPinned() ? iszIndexApply ? dataLength - row.index : 1 : iszIndexApply ? dataLength - row.index : 0,
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        )
      })}

      {parentTaskId ? <AddTaskInput task={currentRow} group_id={group_id} /> : <></>}
    </TableRow>
  )
}

export default DraggableRow
